import { useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { auditService } from '../services/auditService';
import './AuditScheduleConflicts.css';

/** Stable text key for a list of ids, so a new array with the same ids is "the same". */
function idsKey(ids) {
  return (ids || []).map(String).sort().join(',');
}

function AuditScheduleConflicts({
  startAt,
  endAt,
  teamIds,
  dynamicMemberIds,
  excludeAuditId,
  onConflictsChange,
  isEditing = false,
}) {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);

  // The parent form redraws often (and passes new arrays each time); only a
  // real change of dates, teams or members should trigger a new check.
  const teamKey = idsKey(teamIds);
  const memberKey = idsKey(dynamicMemberIds);
  const hasSchedule = Boolean(startAt && endAt);
  const hasTeams = teamKey !== '' || memberKey !== '';

  const notifyRef = useRef(onConflictsChange);
  notifyRef.current = onConflictsChange;
  const lastSentRef = useRef('[]');
  const requestRef = useRef(0);

  const publish = (next) => {
    const signature = JSON.stringify(next);
    if (signature !== lastSentRef.current) {
      lastSentRef.current = signature;
      notifyRef.current?.(next);
    }
  };

  useEffect(() => {
    if (!hasSchedule || !hasTeams) {
      setConflicts([]);
      setChecked(false);
      setLoading(false);
      publish([]);
      return undefined;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await auditService.previewConflicts({
          start_at: startAt,
          end_at: endAt,
          team_ids: teamKey ? teamKey.split(',').map(Number) : [],
          dynamic_member_ids: memberKey ? memberKey.split(',').map(Number) : [],
          exclude_audit_id: excludeAuditId || null,
        });
        if (requestRef.current !== requestId) return; // a newer check started
        const next = data || [];
        setConflicts(next);
        setChecked(true);
        publish(next);
      } catch {
        if (requestRef.current !== requestId) return;
        setConflicts([]);
        setChecked(true);
        publish([]);
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
    // publish only reads refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startAt, endAt, teamKey, memberKey, excludeAuditId, hasSchedule, hasTeams]);

  if (!hasSchedule) {
    return (
      <div className="audit-schedule-conflicts audit-schedule-conflicts--muted">
        Set the new schedule to check for overlapping team assignments.
      </div>
    );
  }

  if (!hasTeams) {
    return (
      <div className="audit-schedule-conflicts audit-schedule-conflicts--muted">
        Select teams or dynamic members to check for schedule clashes.
      </div>
    );
  }

  if (!checked) {
    return (
      <div className="audit-schedule-conflicts audit-schedule-conflicts--loading">
        Checking team schedules...
      </div>
    );
  }

  const refreshing = loading ? (
    <span className="audit-schedule-conflicts__refreshing">Re-checking…</span>
  ) : null;

  if (conflicts.length === 0) {
    return (
      <div className="audit-schedule-conflicts audit-schedule-conflicts--clear">
        No schedule conflicts detected for the selected teams and dates. {refreshing}
      </div>
    );
  }

  const uniqueMembers = new Set(conflicts.map((item) => String(item.user_id))).size;
  // Counts per other schedule (no names: the Manage team screen shows who).
  const bySchedule = new Map();
  conflicts.forEach((item) => {
    (item.conflicting_audits || []).forEach((audit) => {
      const key = String(audit.audit_id);
      const entry = bySchedule.get(key) || { ...audit, people: new Set() };
      entry.people.add(String(item.user_id));
      bySchedule.set(key, entry);
    });
  });
  const schedules = [...bySchedule.values()].sort((a, b) => b.people.size - a.people.size);
  const when = (audit) => {
    try {
      return `${format(parseISO(audit.start_at), 'MMM d, HH:mm')} – ${format(parseISO(audit.end_at), 'MMM d, HH:mm')}`;
    } catch {
      return '';
    }
  };

  return (
    <div className="audit-schedule-conflicts audit-schedule-conflicts--warn">
      <header>
        <strong>
          {uniqueMembers} {uniqueMembers === 1 ? 'person is' : 'people are'} also booked on{' '}
          {schedules.length} other schedule{schedules.length === 1 ? '' : 's'} in this period
        </strong>
        {refreshing}
      </header>

      <ul className="audit-schedule-conflicts__counts">
        {schedules.map((audit) => (
          <li key={audit.audit_id}>
            <b>{audit.people.size}</b>
            <span>
              <span>
                {audit.people.size === 1 ? 'person' : 'people'} also on <strong>{audit.audit_name}</strong>
              </span>
              <small>{when(audit)}</small>
            </span>
          </li>
        ))}
      </ul>

      <p className="audit-schedule-conflicts__hint">
        {isEditing
          ? 'Open Manage team to see who they are and remove or replace them for this schedule only.'
          : 'After saving, the Manpower step shows who they are so you can remove or replace them.'}
      </p>
    </div>
  );
}

export default AuditScheduleConflicts;
